import React, { useState, useEffect, useMemo } from 'react';
import { Veiculo } from '../../types/veiculo';
import { VeiculoAbastecimento } from '../../types/veiculoExtra';
import { veiculoExtraService } from '../../services/veiculoExtraService';
import { Fuel, Plus, History, AlertTriangle, X, TrendingUp, DollarSign, Gauge } from 'lucide-react';

interface Props {
  veiculo: Veiculo | null;
  motoristasList: { id: number; nome: string }[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const VeiculoAbastecimentoModal: React.FC<Props> = ({
  veiculo,
  motoristasList,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<'novo' | 'historico'>('novo');
  const [history, setHistory] = useState<VeiculoAbastecimento[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Form states
  const [kmAnterior, setKmAnterior] = useState<number | ''>('');
  const [kmAtual, setKmAtual] = useState<number | ''>('');
  const [litros, setLitros] = useState<number | ''>('');
  const [valorTotal, setValorTotal] = useState<number | ''>('');
  const [tipoCombustivel, setTipoCombustivel] = useState('diesel');
  const [posto, setPosto] = useState('');
  const [numeroNota, setNumeroNota] = useState('');
  const [motoristaId, setMotoristaId] = useState<number | ''>('');
  const [observacoes, setObservacoes] = useState('');

  useEffect(() => {
    if (veiculo && isOpen) {
      setKmAnterior(veiculo.km_atual || '');
      setKmAtual('');
      setLitros('');
      setValorTotal('');
      setMotoristaId(veiculo.motorista_id || veiculo.agregado_id || '');
      loadHistory();
    }
  }, [veiculo, isOpen]);

  const loadHistory = async () => {
    if (!veiculo) return;
    setLoading(true);
    try {
      const data = await veiculoExtraService.getAbastecimentos(veiculo.id);
      setHistory(data);
      if (data.length > 0 && data[0].km_atual) {
        setKmAnterior(data[0].km_atual);
      }
    } catch (err) {
      console.error('Erro ao carregar histórico de abastecimento:', err);
    } finally {
      setLoading(false);
    }
  };

  // Automatic live calculations for UX
  const calcKmPercorrido = useMemo(() => {
    if (kmAtual && kmAnterior !== '') {
      return Math.max(0, Number(kmAtual) - Number(kmAnterior));
    }
    return 0;
  }, [kmAtual, kmAnterior]);

  const calcPrecoLitro = useMemo(() => {
    if (valorTotal && litros && Number(litros) > 0) {
      return (Number(valorTotal) / Number(litros)).toFixed(2);
    }
    return '0.00';
  }, [valorTotal, litros]);

  const calcConsumoKmL = useMemo(() => {
    if (calcKmPercorrido > 0 && litros && Number(litros) > 0) {
      return (calcKmPercorrido / Number(litros)).toFixed(2);
    }
    return '0.00';
  }, [calcKmPercorrido, litros]);

  const calcCustoKm = useMemo(() => {
    if (calcKmPercorrido > 0 && valorTotal) {
      return (Number(valorTotal) / calcKmPercorrido).toFixed(2);
    }
    return '0.00';
  }, [calcKmPercorrido, valorTotal]);

  if (!isOpen || !veiculo) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kmAtual || Number(kmAtual) <= 0) {
      setErrorMsg('Informe o KM atual válido.');
      return;
    }
    if (!litros || Number(litros) <= 0) {
      setErrorMsg('Informe a quantidade de litros inserida.');
      return;
    }
    if (!valorTotal || Number(valorTotal) <= 0) {
      setErrorMsg('Informe o valor total pago.');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      const selectedMot = motoristasList.find((m) => m.id === Number(motoristaId));
      const newAbastecimento: VeiculoAbastecimento = {
        veiculo_id: veiculo.id,
        motorista_id: motoristaId ? Number(motoristaId) : null,
        motorista_nome: selectedMot?.nome || 'Não informado',
        data_abastecimento: new Date().toISOString(),
        km_anterior: kmAnterior !== '' ? Number(kmAnterior) : null,
        km_atual: Number(kmAtual),
        litros: Number(litros),
        valor_total: Number(valorTotal),
        tipo_combustivel: tipoCombustivel,
        posto_combustivel: posto,
        numero_nota: numeroNota,
        observacoes,
      };

      await veiculoExtraService.createAbastecimento(newAbastecimento);
      await loadHistory();
      setActiveTab('historico');
      if (onSuccess) onSuccess();
    } catch (err: any) {
      console.error('Erro ao registrar abastecimento:', err);
      setErrorMsg(err.message || 'Erro ao registrar abastecimento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-500/20 rounded-lg border border-emerald-500/30 text-emerald-400">
              <Fuel className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base leading-snug">Controle de Abastecimento & Consumo (KM)</h3>
              <p className="text-xs text-slate-400">
                Veículo: <span className="font-semibold text-white">{veiculo.placa || 'Sem Placa'}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs navigation */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-3">
          <button
            type="button"
            onClick={() => setActiveTab('novo')}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === 'novo'
                ? 'border-emerald-600 text-emerald-600 bg-white rounded-t-lg'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Plus className="w-4 h-4" />
            Novo Abastecimento
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('historico')}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === 'historico'
                ? 'border-emerald-600 text-emerald-600 bg-white rounded-t-lg'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <History className="w-4 h-4" />
            Histórico & Médias ({history.length})
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 max-h-[75vh] overflow-y-auto">
          {activeTab === 'novo' ? (
            <form onSubmit={handleSave} className="space-y-5">
              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  {errorMsg}
                </div>
              )}

              {/* KM Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    KM Anterior
                  </label>
                  <input
                    type="number"
                    value={kmAnterior}
                    onChange={(e) => setKmAnterior(e.target.value ? Number(e.target.value) : '')}
                    placeholder="Ex: 120000"
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-slate-50 text-slate-700 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    KM Atual (Hodômetro) *
                  </label>
                  <input
                    type="number"
                    value={kmAtual}
                    onChange={(e) => setKmAtual(e.target.value ? Number(e.target.value) : '')}
                    placeholder="Ex: 120550"
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none font-mono font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    KM Percorrido
                  </label>
                  <div className="w-full text-xs p-2.5 border border-emerald-200 rounded-lg bg-emerald-50 text-emerald-800 font-bold font-mono">
                    {calcKmPercorrido} KM
                  </div>
                </div>
              </div>

              {/* Litros e Valor */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Litros Abastecidos *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={litros}
                    onChange={(e) => setLitros(e.target.value ? Number(e.target.value) : '')}
                    placeholder="Ex: 85.5"
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none font-mono"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Valor Total (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={valorTotal}
                    onChange={(e) => setValorTotal(e.target.value ? Number(e.target.value) : '')}
                    placeholder="Ex: 480.00"
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none font-mono font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Tipo de Combustível
                  </label>
                  <select
                    value={tipoCombustivel}
                    onChange={(e) => setTipoCombustivel(e.target.value)}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none font-medium"
                  >
                    <option value="diesel">Diesel S10 / Arla</option>
                    <option value="gasolina">Gasolina</option>
                    <option value="etanol">Etanol</option>
                    <option value="gnv">GNV</option>
                  </select>
                </div>
              </div>

              {/* Painel de Cálculo Automático */}
              <div className="p-4 bg-emerald-900 text-white rounded-xl grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-300 flex items-center justify-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Média Consumo
                  </div>
                  <div className="text-lg font-black mt-1 font-mono">{calcConsumoKmL} <span className="text-xs font-normal text-emerald-200">KM/L</span></div>
                </div>

                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-300 flex items-center justify-center gap-1">
                    <DollarSign className="w-3 h-3" /> Preço por Litro
                  </div>
                  <div className="text-lg font-black mt-1 font-mono">R$ {calcPrecoLitro}</div>
                </div>

                <div>
                  <div className="text-[10px] uppercase font-bold text-emerald-300 flex items-center justify-center gap-1">
                    <Gauge className="w-3 h-3" /> Custo por KM
                  </div>
                  <div className="text-lg font-black mt-1 font-mono">R$ {calcCustoKm}</div>
                </div>
              </div>

              {/* Posto e Motorista */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Posto / Estabelecimento
                  </label>
                  <input
                    type="text"
                    value={posto}
                    onChange={(e) => setPosto(e.target.value)}
                    placeholder="Ex: Posto Shell BR-101"
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Motorista Responsável
                  </label>
                  <select
                    value={motoristaId}
                    onChange={(e) => setMotoristaId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none"
                  >
                    <option value="">Selecione o motorista...</option>
                    {motoristasList.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow transition-colors flex items-center gap-2"
                >
                  {saving ? 'Registrando...' : 'Salvar Abastecimento'}
                </button>
              </div>
            </form>
          ) : (
            /* Tab de Histórico */
            <div className="space-y-4">
              {loading ? (
                <div className="py-8 text-center text-xs text-slate-500">Carregando abastecimentos...</div>
              ) : history.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-500 border border-dashed rounded-xl p-4">
                  Nenhum abastecimento registrado para este veículo.
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((a) => (
                    <div
                      key={a.id || a.data_abastecimento}
                      className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs"
                    >
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                        <span className="font-semibold text-slate-700">
                          {new Date(a.data_abastecimento).toLocaleDateString('pt-BR')} - {a.posto_combustivel || 'Posto não informado'}
                        </span>
                        <span className="font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full text-xs">
                          R$ {Number(a.valor_total).toFixed(2)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-600">
                        <div>KM Atual: <span className="font-bold text-slate-800">{a.km_atual}</span></div>
                        <div>Litros: <span className="font-bold text-slate-800">{a.litros} L</span></div>
                        <div>Média: <span className="font-bold text-emerald-600">{a.consumo_km_l ? `${a.consumo_km_l} KM/L` : '-'}</span></div>
                        <div>Custo/KM: <span className="font-bold text-slate-800">{a.custo_km ? `R$ ${a.custo_km}` : '-'}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
